import * as HeadlessDropdownMenu from '@radix-ui/react-dropdown-menu'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import React, {
  Children,
  createRef,
  ReactChild,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ThemeProvider, useTheme } from '@opallabs/hooks'
import { Checkbox } from '../../Checkbox'
import { Radio } from '../../Radio'
import { SearchInput } from '../../SearchInput'
import { TextInputImperativeHandle } from '../../TextInputWithLabel'
import { handleClick, handleKeyDown, handleSelect } from '../eventHandlers'
import { entryAnimation, initialAnimationState } from '../menuAnimationConfig'
import * as styles from '../styles'
import { RadioGroupProps } from '../types'

export {
  Root,
  Sub,
  Trigger,
  Arrow,
  ItemIndicator,
} from '@radix-ui/react-dropdown-menu'

export const Portal: React.FC<
  React.ComponentProps<typeof HeadlessDropdownMenu.Portal>
> = ({ children, ...props }) => {
  const theme = useTheme()

  /**
   * HeadlessDropdownMenu.Portal mounts its children outside of the DOM hierarchy, typically
   * directly under the <body> element. Because this will move the components
   * outside of the scope of any CSS variables applied in the existing DOM tree,
   * we need to reprovide Gem's <ThemeProvider> with the current theme to rescope them.
   */
  return (
    <HeadlessDropdownMenu.Portal {...props}>
      <ThemeProvider
        theme={theme}
        // Portalled components should have absolute/fixed position
        // Reset the size properties, so that the portal itself
        // does not affect the container's layout
        css={{ blockSize: 'auto', inlineSize: 'auto' }}
      >
        {children}
      </ThemeProvider>
    </HeadlessDropdownMenu.Portal>
  )
}
Portal.displayName = 'Lapidary: Portal ← DropdownMenu'

export const Content = React.forwardRef<
  React.ElementRef<typeof HeadlessDropdownMenu.Content>,
  Omit<
    React.ComponentPropsWithoutRef<typeof HeadlessDropdownMenu.Content>,
    'asChild'
  > & { zIndex?: number; showArrow?: boolean }
>((props, forwardedRef) => {
  const theme = useTheme()

  const { children, showArrow, zIndex, onEscapeKeyDown, ...otherProps } = props

  return (
    <HeadlessDropdownMenu.Content
      {...otherProps}
      css={styles.list({ theme })}
      ref={forwardedRef}
      onEscapeKeyDown={(e) => {
        e.stopPropagation()
        onEscapeKeyDown?.(e)
      }}
      asChild
    >
      <motion.div
        initial={initialAnimationState}
        animate={entryAnimation}
        style={{
          transformOrigin:
            'var(--radix-dropdown-menu-content-transform-origin)',
          ...(zIndex == undefined ? {} : { zIndex }),
        }}
      >
        {children}
        {showArrow && <HeadlessDropdownMenu.Arrow css={styles.arrow(theme)} />}
      </motion.div>
    </HeadlessDropdownMenu.Content>
  )
})
Content.displayName = 'Lapidary: Content ← DropdownMenu'

export const Item = React.forwardRef<
  React.ElementRef<typeof HeadlessDropdownMenu.Item>,
  { destructive?: boolean } & React.ComponentPropsWithoutRef<
    typeof HeadlessDropdownMenu.Item
  >
>(({ destructive = false, ...props }, forwardedRef) => {
  const theme = useTheme()

  if (props.onClick !== undefined) {
    console.warn(
      'Avoid using the onClick prop for DropdownMenu.Item. To handle item selection, use the onSelect prop instead.'
    )
  }

  return (
    <HeadlessDropdownMenu.Item
      {...props}
      className={classNames('lapidary-dropdown-menu-item', props.className)}
      css={styles.item({ theme, destructive })}
      ref={forwardedRef}
    />
  )
})
Item.displayName = 'Lapidary: Item ← DropdownMenu'

export const SubContent = React.forwardRef<
  React.ElementRef<typeof HeadlessDropdownMenu.SubContent>,
  React.ComponentPropsWithoutRef<typeof HeadlessDropdownMenu.SubContent> & {
    maxHeight?: number
    searchable?: boolean
  }
>((props, forwardedRef) => {
  const theme = useTheme()
  const { searchable, children, ...elementProps } = props
  const [searchValue, setSearchValue] = useState('')
  const searchRef = useRef<TextInputImperativeHandle | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  const onSearchKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation()
    if (e.key === 'ArrowDown') {
      const nextChild = listRef.current?.children[1] as HTMLDivElement
      nextChild.focus()
    }
  }
  const filteredChildren = useMemo(() => {
    if (!searchable || !searchValue) return children
    return Children.toArray(children).filter((reactChild) => {
      const child = reactChild as React.ReactChild
      if (typeof child === 'object') {
        const matchingText = child.props.children.filter(
          (grandchild: ReactChild) =>
            typeof grandchild === 'string' &&
            grandchild.toLowerCase().includes(searchValue.toLocaleLowerCase())
        ) as Array<string>
        return Boolean(matchingText.length)
      }
      return false
    })
  }, [searchValue, searchable, children])

  return (
    <HeadlessDropdownMenu.SubContent
      {...elementProps}
      css={styles.list({ theme })}
      ref={searchable ? listRef : forwardedRef}
    >
      {searchable && (
        <HeadlessDropdownMenu.Item
          onFocus={() => searchRef.current?.focus()}
          onSelect={(e) => e.preventDefault()}
          /* Prevent items from stealing focus from search when hovering */
          onPointerLeave={(e) => e.preventDefault()}
          onPointerMove={(e) => e.preventDefault()}
          onPointerOut={(e) => e.preventDefault()}
        >
          <SearchInput
            ref={searchRef}
            onSearch={setSearchValue}
            onKeyDown={onSearchKeyDown}
            theme={theme}
          />
        </HeadlessDropdownMenu.Item>
      )}
      {filteredChildren}
      {searchable && !Children.count(filteredChildren) && (
        <Item
          disabled={true}
          onFocus={() => searchRef.current?.focus()}
          onSelect={(e) => e.preventDefault()}
        >
          No results match your search
        </Item>
      )}
    </HeadlessDropdownMenu.SubContent>
  )
})
SubContent.displayName = 'Lapidary: SubContent ← DropdownMenu'

export const SubTrigger = React.forwardRef<
  React.ElementRef<typeof HeadlessDropdownMenu.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof HeadlessDropdownMenu.SubTrigger>
>((props, forwardedRef) => {
  const theme = useTheme()

  return (
    <HeadlessDropdownMenu.SubTrigger
      {...props}
      css={[styles.item({ theme }), styles.subMenuTrigger]}
      ref={forwardedRef}
    />
  )
})
SubTrigger.displayName = 'Lapidary: SubTrigger ← DropdownMenu'

export const Separator = React.forwardRef<
  React.ElementRef<typeof HeadlessDropdownMenu.Separator>,
  React.ComponentPropsWithoutRef<typeof HeadlessDropdownMenu.Separator>
>((props, forwardedRef) => {
  const theme = useTheme()

  return (
    <HeadlessDropdownMenu.Separator
      {...props}
      css={styles.separator({ theme })}
      ref={forwardedRef}
    />
  )
})
Separator.displayName = 'Lapidary: Separator ← DropdownMenu'

export const Label = React.forwardRef<
  React.ElementRef<typeof HeadlessDropdownMenu.Label>,
  React.ComponentPropsWithoutRef<typeof HeadlessDropdownMenu.Label>
>((props, forwardedRef) => {
  const theme = useTheme()

  return (
    <HeadlessDropdownMenu.Label
      {...props}
      css={styles.label({ theme })}
      ref={forwardedRef}
    />
  )
})
Label.displayName = 'Lapidary: Label ← DropdownMenu'

export const RadioGroup = React.forwardRef<
  React.ElementRef<typeof HeadlessDropdownMenu.RadioGroup>,
  React.ComponentPropsWithoutRef<typeof HeadlessDropdownMenu.RadioGroup> &
    RadioGroupProps
>((props, forwardedRef) => {
  const theme = useTheme()
  const { name, useChildren = false, children, ...headlessProps } = props

  return (
    <HeadlessDropdownMenu.RadioGroup {...headlessProps} ref={forwardedRef}>
      {useChildren ? (
        children
      ) : (
        <Radio.Group defaultValue={props.value} name={name} theme={theme}>
          {children}
        </Radio.Group>
      )}
    </HeadlessDropdownMenu.RadioGroup>
  )
})
RadioGroup.displayName = 'Lapidary: RadioGroup ← DropdownMenu'

export const RadioItem = React.forwardRef<
  React.ElementRef<typeof HeadlessDropdownMenu.RadioItem>,
  React.ComponentPropsWithoutRef<typeof HeadlessDropdownMenu.RadioItem> & {
    /**
     * When true renders children passed to this component in place of
     * Lapidary Radio Input elements. Standard radio button implementations
     * should not use this.
     * */
    useChildren?: boolean
  }
>(({ children, useChildren = false, ...radixProps }, forwardedRef) => {
  const theme = useTheme()

  const inputRef = createRef<HTMLInputElement>()
  return (
    <HeadlessDropdownMenu.RadioItem
      {...radixProps}
      css={styles.item({ theme })}
      onKeyDown={handleKeyDown(inputRef, radixProps.onKeyDown)}
      onSelect={handleSelect(radixProps.onSelect)}
      onClick={handleClick(inputRef, radixProps.onClick)}
      ref={forwardedRef}
    >
      {useChildren ? (
        children
      ) : (
        <Radio.Input
          label={children as string}
          ref={inputRef}
          value={radixProps.value}
        />
      )}
    </HeadlessDropdownMenu.RadioItem>
  )
})
RadioItem.displayName = 'Lapidary: RadioItem ← DropdownMenu'

export const CheckboxItem = React.forwardRef<
  React.ElementRef<typeof HeadlessDropdownMenu.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof HeadlessDropdownMenu.CheckboxItem>
>((props, forwardedRef) => {
  const theme = useTheme()

  return (
    <HeadlessDropdownMenu.CheckboxItem
      {...props}
      css={styles.item({ theme })}
      onSelect={handleSelect(props.onSelect)}
      ref={forwardedRef}
    >
      <Checkbox
        checked={props.checked as boolean}
        onChange={() => null}
        css={styles.checkboxOverrides}
      />
      {props.children}
    </HeadlessDropdownMenu.CheckboxItem>
  )
})
CheckboxItem.displayName = 'Lapidary: CheckboxItem ← DropdownMenu'
